import { drawing } from '@progress/kendo-drawing';
import { isString } from '../utils/functions.js';
import { START, END, Markers } from '../constants.js';
import { deepExtend } from '../utils/deep-extend.js';
import { CircleMarker } from './CircleMarker.js';
import { ArrowMarker } from './ArrowMarker.js';

const MarkerPathMixin = {
    _getPath: function (position) {
        let path = this.drawingElement;
        if (path instanceof drawing.MultiPath) {
            if (position === START) {
                path = path.paths[0];
            }
            else {
                path = path.paths[path.paths.length - 1];
            }
        }
        if (path && path.segments.length) {
            return path;
        }
    },
    _normalizeMarkerOptions: function (options) {
        const startCap = options.startCap;
        const endCap = options.endCap;
        if (isString(startCap)) {
            options.startCap = {
                type: startCap
            };
        }
        if (isString(endCap)) {
            options.endCap = {
                type: endCap
            };
        }
    },
    _removeMarker: function (position) {
        const marker = this._markers[position];
        if (marker) {
            this.drawingContainer().remove(marker.drawingElement);
            delete this._markers[position];
        }
    },
    _createMarkers: function () {
        const options = this.options;
        this._normalizeMarkerOptions(options);
        this._markers = {};
        this._markers[START] = this._createMarker(options.startCap, START);
        this._markers[END] = this._createMarker(options.endCap, END);
    },
    _createMarker: function (options, position) {
        const type = (options || {}).type;
        const path = this._getPath(position);
        let markerType, marker;
        if (!path) {
            this._removeMarker(position);
            return;
        }
        if (type === Markers.filledCircle) {
            markerType = CircleMarker;
        }
        else if (type === Markers.arrowStart || type === Markers.arrowEnd) {
            markerType = ArrowMarker;
        }
        else {
            this._removeMarker(position);
        }
        if (markerType) {
            marker = new markerType(deepExtend({}, options, {
                position: position
            }));
            marker.positionMarker(path);
            this.drawingContainer().append(marker.drawingElement);
            return marker;
        }
    },
    _positionMarker: function (position) {
        const marker = this._markers[position];
        if (marker) {
            const path = this._getPath(position);
            if (path) {
                marker.positionMarker(path);
            }
            else {
                this._removeMarker(position);
            }
        }
    },
    _capMap: {
        start: 'startCap',
        end: 'endCap'
    },
    _redrawMarker: function (pathChange, position, options) {
        this._normalizeMarkerOptions(options);
        const pathOptions = this.options;
        const cap = this._capMap[position];
        const pathCapType = (pathOptions[cap] || {}).type;
        const optionsCap = options[cap];
        let created = false;
        if (optionsCap) {
            pathOptions[cap] = deepExtend({}, pathOptions[cap], optionsCap);
            if (optionsCap.type && pathCapType !== optionsCap.type) {
                this._removeMarker(position);
                this._markers[position] = this._createMarker(pathOptions[cap], position);
                created = true;
            }
            else if (this._markers[position]) {
                this._markers[position].redraw(optionsCap);
            }
        }
        else if (pathChange && !this._markers[position] && pathOptions[cap]) {
            this._markers[position] = this._createMarker(pathOptions[cap], position);
            created = true;
        }
        return created;
    },
    _redrawMarkers: function (pathChange, options) {
        if (!this._redrawMarker(pathChange, START, options) && pathChange) {
            this._positionMarker(START);
        }
        if (!this._redrawMarker(pathChange, END, options) && pathChange) {
            this._positionMarker(END);
        }
    }
};

export { MarkerPathMixin };
